import {
  type ExpandedStateList,
  assignPrototypeAPIs,
  assignTableAPIs,
  type Row,
  type RowData,
  type Table_Internal,
  type TableFeature,
  type TableFeatures,
} from '@tanstack/react-table';
import {
  row_getCanExpand,
  table_getCanSomeRowsExpand,
  table_getVisibleLeafColumns,
  table_setExpanded,
} from '@tanstack/react-table/static-functions';

import type {
  ExpandedState,
  RowSubRowsFeatureConstructors,
} from './sub-rows-types.js';
import { calculateMaxVisibleDepth, isRowDisabled } from './sub-rows-utils.js';
import { table_remeasureColumn } from '../VirtualScrolling/VirtualScrolling.js';

/**
 * Helper function to determine if all rows are expanded, based on the current expanded state and the flat row model.
 * @internal
 */
export function table_getIsAllRowsExpanded<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  expandedState: ExpandedState,
  flatRows: Row<TFeatures, TData>[],
): boolean {
  // If expandedState is true, save some cycles and return true.
  if (typeof expandedState === 'boolean') {
    return expandedState === true;
  }

  // If expandedState is an object, but has no values, return false immediately
  if (!Object.keys(expandedState).length) {
    return false;
  }

  // If any row that can be expanded is not expanded, return false
  if (
    // The original code from @tanstack/react-table does not check if a row is even
    // expandable before it counts it as isExpanded.
    flatRows.some((row) => {
      return (
        row_getCanExpand(row) &&
        !row_getIsExpanded(row) &&
        !row_getCanExpandDisabled(row) &&
        !table.getIsAncestorDisabled(row)
      );
    })
  ) {
    return false;
  }

  // They must all be expanded :shrug:
  return true;
}

/**
 * Helper function to toggle allRows expanded state.
 * @internal
 */
export function table_toggleAllRowsExpanded<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): void {
  if (
    !table_getIsAllRowsExpanded(
      table,
      table.atoms.expanded?.get() ?? {},
      table.getRowModel().flatRows,
    )
  ) {
    const newExpandedState: Record<string, boolean> = table
      .getExpandedRowModel()
      .flatRows.reduce((expanded, row) => {
        if (row_getCanExpand(row) && !row_getCanExpandDisabled(row)) {
          expanded[row.id] = true;
        }
        return expanded;
      }, {} as Record<string, boolean>);
    table_setExpanded(table, newExpandedState);
  } else {
    table_setExpanded(table, {});
  }
}

/**
 * Helper function to find the columnId in which the indicator should be rendered.
 * @internal
 */
export function table_getSubRowIndicatorColumnId<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  enableSubRows?: boolean,
  subrowColumnId?: string,
): string | null {
  if (enableSubRows !== true) {
    return null;
  }

  // If a subrowColumnId is defined in the options, we can immediately
  // use the configured id and do not need to look for the first visible one.
  if (subrowColumnId) {
    table_remeasureColumn(table, subrowColumnId);
    return subrowColumnId;
  }

  const visibleLeafColumns = table_getVisibleLeafColumns(table);

  for (const column of visibleLeafColumns) {
    const columnDef = column.columnDef;
    if (columnDef.meta && columnDef.meta.builtin !== true) {
      table_remeasureColumn(table, column.id);
      return column.id;
    }
  }
  return null;
}

/**
 * Helper function to calculate the maximum open depth of the subrows based on the current expanded state.
 * @internal
 */
export function table_getSubRowOpenDepth<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  expandedState: ExpandedState,
): number {
  const expandedRowIds: string[] = [];

  if (expandedState === true) {
    const rows = table.getExpandedRowModel().rows;

    // all rows are expanded
    for (const row of rows) {
      if (row.subRows.length > 0) {
        // filter out leafs, as they would also not be present in the expanded state
        expandedRowIds.push(row.id);
      }
    }
  } else {
    for (const rowId in expandedState) {
      if (expandedState[rowId]) {
        // only include rows which are actually expanded: true
        expandedRowIds.push(rowId);
      }
    }
  }

  return calculateMaxVisibleDepth(expandedRowIds, table.options.rowIdSeparator);
}

/**
 * Helper function to determine if the row or some of its ancestors are disabled.
 * @internal
 */
export function table_getIsAncestorDisabled<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  expandedState: ExpandedState,
  row: Row<TFeatures, TData>,
): boolean {
  table._disabledSubRowsCache ??= new Map<string, boolean>();
  return isRowDisabled(table._disabledSubRowsCache, row);
}

/**
 * Helper function to determine if the table has at least one row with at least one sub-row.
 * @internal
 */
export function table_getHasSubRows<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): boolean {
  return (
    table_getCanSomeRowsExpand(table) ||
    table_getIsAllRowsExpanded(
      table,
      table.atoms.expanded?.get() ?? {},
      table.getRowModel().flatRows,
    )
  );
}

type RowLineConfigShape = 'Line' | 'TShape' | 'LShape' | 'Empty';

export function table_getRowLineConfig<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  rowId: string,
): Array<RowLineConfigShape> {
  if (!rowId) {
    return [];
  }
  const lineConfig: Array<RowLineConfigShape> = [];
  const row = table.getRow(rowId);

  for (let i = 1; i <= row.depth; i += 1) {
    if (i === row.depth) {
      const isLastExpandedRowOnLevel = row.getIsLastExpandedOnLevel();
      if (isLastExpandedRowOnLevel) {
        lineConfig.push('LShape');
        continue;
      }
      lineConfig.push('TShape');
      continue;
    }
    lineConfig.push('Line');
  }

  // Make sure the rowLineConfig exists on the table instance.
  table._rowLineConfig ??= new Map<string, Array<RowLineConfigShape>>();

  // Fill the empty gaps and inherit the ones from the parent as well.
  const parentId = row.parentId;
  if (parentId) {
    const parentShapes =
      table._rowLineConfig.get(parentId) ?? table.getRowLineConfig(parentId);
    if (parentShapes.at(-1) === 'LShape') {
      lineConfig[lineConfig.length - 2] = 'Empty';
    }
    for (const [index, shape] of parentShapes.entries()) {
      if (shape === 'Empty') {
        lineConfig[index] = 'Empty';
      }
    }
  }

  // Populate the cache with the stored items.
  table._rowLineConfig.set(rowId, lineConfig);
  return lineConfig;
}

/**
 * Helper function to toggle the expanded state of a specific row.
 * @internal
 */
export function row_toggleExpanded<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>, expanded?: boolean): void {
  table_setExpanded(row.table, (prev) => {
    // true, if the current row was expanded before
    const exists = prev === true ? true : !!prev?.[row.id];

    let prevExpandedState: ExpandedStateList = {};
    if (prev === true) {
      // ALL rows were expanded before
      row.table.getCoreRowModel().flatRows.forEach((row) => {
        if (
          !row.getCanExpandDisabled() ||
          !row.table.getIsAncestorDisabled(row)
        ) {
          prevExpandedState[row.id] = true;
        }
      });
    } else {
      prevExpandedState = prev;
    }

    const shouldExpand = expanded ?? !exists;
    if (!exists && shouldExpand) {
      // row should expand and was not expanded before → add to new expand state
      return {
        ...prevExpandedState,
        [row.id]: true,
      };
    }

    if (exists && !shouldExpand) {
      // row was expanded before and should be collapsed
      for (const key in prevExpandedState) {
        // also collapse all its sub-rows
        if (key.startsWith(row.id)) {
          delete prevExpandedState[key];
        }
      }
      // remove row itself from expand state
      const { [row.id]: _, ...rest } = prevExpandedState;
      return rest;
    }

    // row should not expand and also was not expanded before
    // do nothing and return old state
    return prev;
  });
}

/**
 * Helper function to determine if the given row is the last expanded row on its level.
 * @internal
 */
export function row_getIsLastExpandedOnLevel<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>, orderedFlatRows: Row<TFeatures, TData>[]) {
  const parentRow = row.getParentRow();
  const orderedSiblings = orderedFlatRows.find(
    (row: Row<TFeatures, TData>) => row.id === parentRow?.id,
  )?.subRows;
  const lastSibling = orderedSiblings?.at(-1)?.id;
  return lastSibling === row.id;
}

/**
 * Helper function to get the last expanded sub-row of the given row.
 * @internal
 */
export function row_getLastExpandedSubRow<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>): Row<TFeatures, TData> | undefined {
  const orderedRow = row.table
    .getRowOrderRowModel()
    .flatRows.find(
      (sortedRow: Row<TFeatures, TData>) => sortedRow.id === row.id,
    );

  return (
    orderedRow
      ?.getLeafRows()
      // only consider sub-rows that are actually expanded
      .findLast((subRow: Row<TFeatures, TData>) => {
        const subRowParent = subRow.getParentRow();
        if (subRowParent) {
          return row_getIsExpanded(subRowParent);
        }
        return false;
      })
  );
}

/**
 * Helper function to determine if the given row is expanded based on the current expanded state.
 * @internal
 */
export function row_getIsExpanded<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>) {
  const expanded = row.table.atoms.expanded?.get();
  return !!(
    (expanded === true || expanded?.[row.id]) &&
    !row.table.getIsAncestorDisabled(row)
  );
}

/**
 * Determines whether the given subRow expander is disabled.
 * @internal
 */
export function row_getCanExpandDisabled<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>) {
  return row.table.options.getRowExpandDisabled?.(row.original) ?? false;
}

/**
 * Helper function to calculate the depth of the row based on its id and the configured rowIdSeparator.
 * @internal
 */
export function row_getSubRowDepth<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>) {
  return row.id.split(row.table.options.rowIdSeparator || '.').length - 1;
}

/**
 * Constructs the column row subrows for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructRowSubRowsFeature<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(): TableFeature<RowSubRowsFeatureConstructors<TFeatures, TData>> {
  return {
    assignRowPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableRowSubRowsFeature', prototype, table, {
        row_toggleExpanded: {
          fn: (row, expanded) => row_toggleExpanded(row, expanded),
        },
        row_getIsLastExpandedOnLevel: {
          fn: (row, orderedFlatRows) =>
            row_getIsLastExpandedOnLevel(row, orderedFlatRows),
          memoDeps: (row) => [
            table.getRowOrderRowModel().flatRows,
            table.atoms.expanded?.get(),
            row.id,
          ],
        },
        row_getLastExpandedSubRow: {
          fn: (row) => row_getLastExpandedSubRow(row),
          memoDeps: (row) => [
            table.atoms.rowOrder?.get(),
            table.atoms.sorting?.get(),
            table.atoms.expanded?.get(),
            table.options.data,
            row.id,
          ],
        },
        row_getIsExpanded: {
          fn: (row) => row_getIsExpanded(row),
        },
        row_getCanExpandDisabled: {
          fn: (row) => row_getCanExpandDisabled(row),
        },
        row_getSubRowDepth: { fn: (row) => row_getSubRowDepth(row) },
      });
    },
    constructTableAPIs: (table) => {
      assignTableAPIs('DataTableRowSubRowsFeature', table, {
        table_getIsAllRowsExpanded: {
          fn: (expandedState, flatRows) =>
            table_getIsAllRowsExpanded(table, expandedState, flatRows),

          memoDeps: () => [
            table.atoms.expanded?.get(),
            table.getRowModel().flatRows,
          ],
        },
        table_toggleAllRowsExpanded: {
          fn: () => table_toggleAllRowsExpanded(table),
        },
        table_getSubRowIndicatorColumnId: {
          fn: (enableSubRows, subrowColumnId) =>
            table_getSubRowIndicatorColumnId(
              table,
              enableSubRows,
              subrowColumnId,
            ),
          memoDeps: () => [
            table.options.enableExpanding,
            table.options.subRowColumnId,
            table.atoms.columnVisibility?.get(),
            table.options.columns,
          ],
        },
        table_getSubRowOpenDepth: {
          fn: (expandedState) => table_getSubRowOpenDepth(table, expandedState),
          memoDeps: () => [table.atoms.expanded?.get()],
        },
        table_getIsAncestorDisabled: {
          fn: (row, expandedState) =>
            table_getIsAncestorDisabled(table, expandedState, row),
          memoDeps: (row) => [row, table.getPreExpandedRowModel().flatRows],
        },
        table_getRowLineConfig: {
          fn: (rowId) => table_getRowLineConfig(table, rowId),
          memoDeps: (rowId) => [
            rowId,
            table.getRowOrderRowModel().flatRows,
            table.atoms.expanded?.get(),
          ],
        },
        table_getHasSubRows: {
          fn: () => table_getHasSubRows(table),
        },
      });
    },
  };
}

/**
 * Feature implementation for Row Details.
 * @internal
 */
export const DataTableRowSubRowsFeature = constructRowSubRowsFeature();
